// Posteingang: automatischer Rechnungsimport aus dem separaten Rechnungspostfach (IMAP, nur lesend). Zugangsdaten stehen nur auf dem Server.
import { useState, useEffect, useCallback } from "react";
import THEME from "../../../../theme";
import { fs } from "../../utils/styles";
import { belegeApi } from "../../../belege/belegeApi";
import { Karte, Klein, Meldung, Chip, Bestaetigung, datumZeit, useAktion } from "../../../belege/ui";

const MODUS_TEXT = { aus: "ausgeschaltet", trocken: "Probelauf (nur ansehen)", live: "aktiv (legt Belege an)" };

export default function BelegePostfach({ geheZu }) {
  const [s, setS] = useState(null);
  const [fehler, setFehler] = useState("");
  const [hinweis, setHinweis] = useState("");
  const [laeuft, setLaeuft] = useState(false);
  const [muster, setMuster] = useState("");
  const [lieferant, setLieferant] = useState("");
  const [frage, setFrage] = useState(null);
  const aktion = useAktion(setFehler, setHinweis);
  const laden = useCallback(() => { belegeApi.postfachStatus().then(setS).catch(e => setFehler(e.message)); }, []);
  useEffect(() => { laden(); }, [laden]);

  const lauf = async (modus) => {
    setLaeuft(true);
    const r = await aktion(() => belegeApi.postfachLauf(modus), (x) => `Lauf ${x.lauf_id} (${x.modus}): ${x.neu} neue Mail(s), ${x.belege} Beleg(e), ${x.rueckfragen} Rückfrage(n), ${x.fehler} Fehler.${x.fehler_text ? ` Fehler: ${x.fehler_text}` : ""}`);
    setLaeuft(false); laden(); return r;
  };
  if (!s) return <div>{fehler ? <Meldung fehler={fehler} /> : "Lädt…"}</div>;
  const k = s.konfiguration;
  const konfiguriert = k.host && k.benutzer && k.passwort;
  return (
    <div>
      <Meldung fehler={fehler} hinweis={hinweis} />
      <Karte titel="✉ Rechnungspostfach (automatischer Abruf)" rechts={<Chip text={MODUS_TEXT[k.modus]} farbe={k.modus === "live" ? THEME.colors.success : k.modus === "trocken" ? THEME.colors.warning : THEME.colors.textMuted} />}>
        <div style={{ display: "grid", gap: 4 }}>
          <div>{k.host ? "✅" : "⚠️"} Server eingetragen · {k.benutzer ? "✅" : "⚠️"} Benutzer eingetragen · {k.passwort ? "✅" : "⚠️"} Passwort eingetragen</div>
        </div>
        {!konfiguriert && <Klein farbe={THEME.colors.warning}>Das Postfach ist noch nicht angebunden. Server, Benutzer und Passwort des eigenen Rechnungspostfachs werden einmalig auf dem Server hinterlegt (nicht hier) – bis dahin passiert nichts.</Klein>}
        {k.modus === "aus" && <Klein>Der Abruf ist per Schalter auf dem Server ausgeschaltet. Schalter: BELEGE_IMAP_MODUS = trocken (Probe) oder live.</Klein>}
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          <button style={fs.btnGray} disabled={laeuft || k.modus === "aus" || !konfiguriert} onClick={() => lauf("trocken")}>🔎 Probelauf (zeigt nur, was importiert würde)</button>
          <button style={fs.btn} disabled={laeuft || k.modus !== "live" || !konfiguriert} onClick={() => setFrage(true)}>⬇ Jetzt abrufen (Belege anlegen)</button>
        </div>
        <Klein>Das Postfach wird nur <b>gelesen</b> – es wird nichts gelöscht, verschoben oder als gelesen markiert, und Links in Mails werden nie geöffnet. Mails von Absendern, die nicht auf der Positivliste stehen, kommen als „Rückfrage“ herein (Schutz vor gefälschten Rechnungen).</Klein>
      </Karte>

      <Karte titel="Erlaubte Absender (Positivliste)">
        <div style={{ display: "grid", gap: 6 }}>
          {s.absender.filter(a => a.aktiv).map(a => (
            <div key={a.id} style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", border: `1px solid ${THEME.colors.border}`, borderRadius: THEME.radius.md, padding: "8px 12px" }}>
              <div><strong>{a.muster}</strong>{a.lieferant ? <span style={{ color: THEME.colors.textSecondary }}> → {a.lieferant}</span> : null}</div>
              <button style={fs.btnDanger} onClick={() => aktion(() => belegeApi.absenderWeg(a.id)).then(laden)}>Entfernen</button>
            </div>
          ))}
          {s.absender.filter(a => a.aktiv).length === 0 && <div style={{ color: THEME.colors.textMuted }}>Noch kein Absender erlaubt – alle Mails würden als Rückfrage eingehen.</div>}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          <input style={{ ...fs.input, flex: "1 1 220px" }} placeholder="Adresse oder Domain, z. B. rechnung@lieferant.de oder @lieferant.de" value={muster} onChange={e => setMuster(e.target.value)} />
          <input style={{ ...fs.input, flex: "1 1 180px" }} placeholder="Lieferantenname (optional)" value={lieferant} onChange={e => setLieferant(e.target.value)} />
          <button style={fs.btn} disabled={muster.trim().length < 5} onClick={() => aktion(() => belegeApi.absenderNeu(muster.trim(), lieferant.trim())).then(() => { setMuster(""); setLieferant(""); laden(); })}>+ Erlauben</button>
        </div>
      </Karte>

      <Karte titel="Letzte Läufe">
        <div style={{ display: "grid", gap: 8 }}>
          {s.laeufe.map(l => (
            <div key={l.id} style={{ border: `1px solid ${THEME.colors.border}`, borderRadius: THEME.radius.md, padding: "10px 12px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                <strong>{datumZeit(l.gestartet_am)}</strong>
                <span><Chip text={l.modus} farbe={THEME.colors.textMuted} /> <Chip text={l.ergebnis || "läuft"} farbe={l.ergebnis === "ok" ? THEME.colors.success : l.ergebnis === "fehler" ? THEME.colors.danger : THEME.colors.warning} /></span>
              </div>
              <Klein>{l.mails_gesehen} gesehen · {l.mails_neu} neu · {l.belege_neu} Beleg(e) · {l.rueckfragen} Rückfrage(n) · {l.fehler} Fehler</Klein>
              {l.details && <Klein>{l.details}</Klein>}
            </div>
          ))}
          {s.laeufe.length === 0 && <div style={{ color: THEME.colors.textMuted }}>Noch kein Lauf.</div>}
        </div>
        {s.laeufe.some(l => l.rueckfragen > 0) && <button style={{ ...fs.btnGray, marginTop: 10 }} onClick={() => geheZu("belege", { status: "rueckfrage" })}>Rückfragen ansehen</button>}
      </Karte>
      {frage && <Bestaetigung titel="Rechnungspostfach jetzt abrufen?" text={"Neue Mails der letzten 14 Tage werden gelesen und als Belege angelegt (Postfach bleibt unverändert).\nDas Anlegen von Belegen ist umkehrbar – gesendet oder gebucht wird dabei nichts."} okText="Jetzt abrufen" onAbbruch={() => setFrage(null)} onOk={() => { setFrage(null); lauf("live"); }} />}
    </div>
  );
}
